import { Config } from 'config/envConfig';
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBars,
    faComments,
    faRobot,
    faFileExport,
    faGear,
    faHeadset,
    faListDots,
    faPieChart,
    faTableCells,
    faDatabase,
    faSync,
    faCaretDown,
    faUsersGear,
    faListCheck,
    faEllipsisV,
} from '@fortawesome/free-solid-svg-icons';
import {
    Button,
    Dropdown,
    alert,
    AlertType,
    NoInteractionView,
} from '@quyva/shared-ui';
import { appLogo, appName } from '../Constants';
import Sidebar from './sidebar.jsx';
import { SupportSideBar } from './support/supportSidebar.jsx';
import { SettingsSideBar } from './settings/settingsSidebar.jsx';
import { useGlobalCredits } from '../context/GlobalStateContext.js';
import axios from 'axios';
import { NotificationBell } from './notifications/notificationBell.jsx';
import ReportLayoutsSideView from './reportLayouts/reportLayoutsSideView.jsx';
import { getUrl } from './pdfDisplay/utils.js';
import { SUBSCRIPTION_PLANS } from '../utils/planUtils';
import { useFeatureFlags } from '../context/FeatureFlagsContext.js';
import {
    ModelTiers,
    Features,
    isFeatureEnabled,
    OtherFeatures,
    ProcessType,
} from '@quyva/common';

const buildCategorizedCustomModelSubmenuItems = (
    categorizedModels,
    modelTier,
    handleModelChangeFn
) => {
    const submenuItems = [];
    let isFirstCategory = true;

    const sortedCategories = Object.keys(categorizedModels).sort();

    for (const category of sortedCategories) {
        const models = categorizedModels[category];

        if (models && models.length > 0) {
            // Add a divider before each new category, except the first one
            if (!isFirstCategory) {
                submenuItems.push({ isDivider: true });
            }

            // Add the heading for the category
            submenuItems.push({
                label: category, // e.g., "Gemini", "OpenAI"
                isHeading: true, // Special flag for rendering
            });

            // Add the models under this category
            models.forEach((modelName) => {
                submenuItems.push({
                    label: modelName,
                    action: () => handleModelChangeFn(modelTier, modelName),
                });
            });

            isFirstCategory = false;
        }
    }

    // Handle case where categorizedModels is empty or all categories are empty
    if (submenuItems.length === 0) {
        return [
            {
                label: 'No custom models available',
                disabled: true,
                action: () => {},
            },
        ];
    }

    return submenuItems;
};

//import Notifications from './notifications.jsx';
const SUPERADMIN_DEMO_INVALID_MODEL = 'Demo Invalid LLM Model';

// Renders offscreen, clipped by a 0x0 overflow-hidden ancestor so it never
// paints or affects page scroll, but still gets laid out — giving us each
// button's true rendered width (padding/border/font included) to measure
// against, instead of guessing at fixed pixel breakpoints.
const MeasureNavButton = React.forwardRef(({ icon, label }, ref) => (
    <button
        ref={ref}
        tabIndex={-1}
        aria-hidden='true'
        className='h-10 px-3 flex items-center justify-center gap-2 border rounded-xl'
    >
        <FontAwesomeIcon icon={icon} />
        <span className='font-semibold text-sm whitespace-nowrap'>
            {label}
        </span>
    </button>
));

const Navbar = (props) => {
    const { featureFlagsData } = useFeatureFlags();
    const navigate = useNavigate();
    const location = useLocation();
    const path = location.pathname;
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const {
        availableCustomModels,
        isLoadingModels,
        selectedTier,
        customModel,
        setSelectedTier,
        setCustomModel,
        transactionMode,
        setTransactionMode,
        isAdmin,
        isOrgAdmin,
        isSuperAdmin,
    } = useGlobalCredits();
    const user = JSON.parse(localStorage.getItem('current'));
    const authToken = encodeURIComponent(user.token);
    const [planName, setPlanName] = useState(SUBSCRIPTION_PLANS.FREE);
    const [isLoadingPlan, setIsLoadingPlan] = useState(true);
    const [canAccessBilling, setCanAccessBilling] = useState(false);

    const safeNavigate = (to) => {
        try {
            localStorage.setItem('last-route', to);
        } catch (e) {
            // ignore
        }
        navigate(to);
    };

    // ALERT notifications belong to the bell icon, everything else to Tasks.
    // Without this split one downgrade notice lit both indicators at once.
    const isTaskNotification =
        !!props.newNotification &&
        props.newNotification.process !== ProcessType.ALERT;

    useEffect(() => {
        if (path !== '/tasks') return;
        if (!isTaskNotification) return;
        if (props.setNewNotification) {
            props.setNewNotification(null);
        }
    }, [path, isTaskNotification, props.setNewNotification]);

    // Save user model preferences when changed
    const saveModelPreferences = async (tier, model = null) => {
        try {
            const response = await axios.put(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${
                        Config.FAST_API_PORT
                    }/model_preferences_routes/api/model-preferences/${encodeURIComponent(
                        user.email
                    )}`
                ),
                null,
                {
                    params: {
                        model_tier: tier,
                        custom_model: model,
                    },
                }
            );

            if (response.data) {
                alert(AlertType.SUCCESS, 'Model preferences updated');
            }
        } catch (error) {
            console.error('Error saving model preferences:', error);
            alert(AlertType.ERROR, 'Failed to save model preferences');
        }
    };

    // Update model preferences
    const handleModelChange = (tier, model = null) => {
        setSelectedTier(tier);
        setCustomModel(model);
        saveModelPreferences(tier, model);
    };

    const modelsForUser = isAdmin
        ? availableCustomModels
        : Object.fromEntries(
              Object.entries(availableCustomModels || {}).filter(
                  ([key]) => key === 'Gemini' || key === 'OpenAI'
              )
          );

    const customSubmenuModelItems = buildCategorizedCustomModelSubmenuItems(
        modelsForUser,
        ModelTiers.CUSTOM,
        handleModelChange
    );

    if (
        isSuperAdmin &&
        !customSubmenuModelItems.some(
            (item) => item.label === SUPERADMIN_DEMO_INVALID_MODEL
        )
    ) {
        customSubmenuModelItems.push({ isDivider: true });
        customSubmenuModelItems.push({ label: 'Testing', isHeading: true });
        customSubmenuModelItems.push({
            label: SUPERADMIN_DEMO_INVALID_MODEL,
            action: () =>
                handleModelChange(
                    ModelTiers.CUSTOM,
                    SUPERADMIN_DEMO_INVALID_MODEL
                ),
        });
    }

    // Build dropdown items from available models
    const modelDropdownItems = [
        {
            label: 'Standard',
            action: () => handleModelChange(ModelTiers.STANDARD),
        },
        {
            label: 'Precision',
            action: () => handleModelChange(ModelTiers.PRECISION),
        },
        {
            label: 'Custom',
            isSubmenu: true,
            items: customSubmenuModelItems,
        },
    ];

    const navigateTo = (to) => {
        if (transactionMode) {
            setTransactionMode(!transactionMode);
        }
        if (path !== to) {
            safeNavigate(to);
        }
    };

    //responsiveness related
    const [isSmallScreen, setIsSmallScreen] = useState(
        window.innerWidth <= 1024
    );

    useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth <= 1024);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Always-visible nav buttons (Tasks has no feature flag — it's never
    // collapsed) — used both for the live row and for measuring how much
    // width they actually take up.
    const fixedNavItems = [];
    if (isFeatureEnabled(Features.CHATS, featureFlagsData)) {
        fixedNavItems.push({ key: 'chats', label: 'Chat', icon: faComments });
    }
    if (isFeatureEnabled(Features.EXTRACTION, featureFlagsData)) {
        fixedNavItems.push({
            key: 'extraction',
            label: 'Extraction',
            icon: faFileExport,
        });
    }
    fixedNavItems.push({ key: 'tasks', label: 'Tasks', icon: faListCheck });

    // Secondary sections, in priority order — the first `visibleFlexCount` of
    // these live in the always-visible capsule (at their designated slot
    // relative to Tasks), the rest fall into "More". How many fit is
    // measured from real rendered button widths (see the hidden measurement
    // row below) rather than guessed pixel breakpoints, so it stays correct
    // regardless of zoom, font metrics, or which admin-only items apply.
    // Requested nav order: Chat, Extraction, Templates/Forms, Dashboard,
    // Tools/Agents, Tasks, Agent Logs, Help.
    const flexNavItems = [];
    if (isFeatureEnabled(Features.TEMPLATE, featureFlagsData)) {
        flexNavItems.push({
            key: 'templates',
            label: 'Templates/Forms',
            icon: faPieChart,
            path: '/templates-and-layouts',
            slot: 'beforeTasks',
        });
    }
    if (isFeatureEnabled(Features.DASHBOARD, featureFlagsData)) {
        flexNavItems.push({
            key: 'dashboard',
            label: 'Dashboard',
            icon: faTableCells,
            path: '/dashboard',
            slot: 'beforeTasks',
        });
    }
    if (
        isFeatureEnabled(Features.TOOLS_AND_AGENTS, featureFlagsData) &&
        isOrgAdmin
    ) {
        flexNavItems.push({
            key: 'toolsAgents',
            label: 'Tools/Agents',
            icon: faUsersGear,
            path: '/agents',
            slot: 'beforeTasks',
        });
    }
    if (isOrgAdmin) {
        flexNavItems.push({
            key: 'agentLogs',
            label: 'Agent Logs',
            icon: faSync,
            path: '/agents-updates',
            slot: 'afterTasks',
        });
    }
    if (isFeatureEnabled(Features.SUPPORT, featureFlagsData)) {
        flexNavItems.push({
            key: 'help',
            label: 'Help',
            icon: faHeadset,
            path: '/help',
            slot: 'afterTasks',
        });
    }

    const [visibleFlexCount, setVisibleFlexCount] = useState(
        flexNavItems.length
    );

    const pillWrapperRef = useRef(null);
    const measureContainerRef = useRef(null);
    const fixedMeasureRefs = useRef([]);
    const flexMeasureRefs = useRef([]);
    const moreMeasureRef = useRef(null);

    const recomputeVisibleFlexCount = () => {
        const container = measureContainerRef.current;
        const pillWrapper = pillWrapperRef.current;
        if (!container || !pillWrapper) return;

        const style = window.getComputedStyle(container);
        const gapPx =
            parseFloat(style.columnGap || style.gap) ||
            6; /* gap-1.5 fallback */
        const paddingPx =
            (parseFloat(style.paddingLeft) || 0) +
            (parseFloat(style.paddingRight) || 0);

        const fixedWidths = fixedMeasureRefs.current
            .filter(Boolean)
            .map((el) => el.offsetWidth);
        const flexWidths = flexMeasureRefs.current
            .filter(Boolean)
            .map((el) => el.offsetWidth);
        const moreWidth = moreMeasureRef.current
            ? moreMeasureRef.current.offsetWidth
            : 0;

        const fixedTotal =
            fixedWidths.reduce((sum, w) => sum + w, 0) +
            gapPx * Math.max(fixedWidths.length - 1, 0) +
            paddingPx;
        const widthWithNFlex = (n) => {
            if (n <= 0) return fixedTotal;
            return (
                fixedTotal +
                gapPx * n +
                flexWidths.slice(0, n).reduce((sum, w) => sum + w, 0)
            );
        };
        const moreIncrement = moreWidth > 0 ? moreWidth + gapPx : 0;
        const available = pillWrapper.clientWidth;

        const countFit = (budget) => {
            for (let n = flexWidths.length; n >= 0; n--) {
                if (widthWithNFlex(n) <= budget) return n;
            }
            return 0;
        };

        let n = countFit(available);
        if (n < flexWidths.length) {
            // Not everything fits — "More" will show, which itself takes
            // space, so re-count against the budget left after reserving it.
            n = countFit(available - moreIncrement);
        }

        setVisibleFlexCount(n);
    };

    useLayoutEffect(() => {
        recomputeVisibleFlexCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [featureFlagsData, isOrgAdmin]);

    useEffect(() => {
        if (typeof ResizeObserver === 'undefined' || !pillWrapperRef.current) {
            return;
        }
        const observer = new ResizeObserver(() => recomputeVisibleFlexCount());
        observer.observe(pillWrapperRef.current);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [featureFlagsData, isOrgAdmin]);

    const visibleFlexItems = flexNavItems.slice(0, visibleFlexCount);
    const overflowFlexItems = flexNavItems.slice(visibleFlexCount);
    const visibleBeforeTasks = visibleFlexItems.filter(
        (item) => item.slot === 'beforeTasks'
    );
    const visibleAfterTasks = visibleFlexItems.filter(
        (item) => item.slot === 'afterTasks'
    );

    const moreMenuItems = overflowFlexItems.map((item) => ({
        label: item.label,
        icon: item.icon,
        selected: path === item.path,
        action: () => navigateTo(item.path),
    }));
    const isInMoreMenu = moreMenuItems.some((item) => item.selected);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
    };

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const closeSidebar = () => {
        setIsSidebarOpen(false);
    };

    const flagsNotLoaded = !featureFlagsData;
    const orgNotLoaded = typeof isOrgAdmin === 'undefined';

    return flagsNotLoaded || orgNotLoaded ? (
        <NoInteractionView viewText={'Loading ...'} />
    ) : (
        <div className='rounded-xl bg-backgrounds-white border flex justify-between items-center h-14 py-0 px-3 select-none'>
            <div className='lg:hidden relative'>
                <button className='ml-2' onClick={toggleSidebar}>
                    <FontAwesomeIcon
                        icon={faListDots}
                        className='text-2xl text-backgrounds-primary200'
                    />
                </button>
            </div>
            <div className='lg:hidden relative flex-1 flex justify-center'>
                <img
                    className='w-10 h-10 '
                    src={appLogo}
                    alt={appName}
                    draggable='false'
                />
            </div>
            <div
                ref={pillWrapperRef}
                className='lg:flex flex-1 min-w-0 justify-start relative'
            >
                <div className='hidden lg:flex items-center gap-1.5 min-w-0 overflow-hidden bg-backgrounds-slate rounded-2xl px-2 py-1.5'>
                    {isFeatureEnabled(Features.CHATS, featureFlagsData) && (
                        <button
                            className={`h-10 px-3 flex items-center justify-center gap-2 shrink-0 border ${
                                path === '/chats'
                                    ? 'bg-backgrounds-primary100 text-texts-tertiary shadow-inner border-backgrounds-primary200'
                                    : 'text-texts-secondary300 bg-backgrounds-white border-transparent'
                            } rounded-xl hover:bg-backgrounds-primary100 hover:border-backgrounds-primary200`}
                            title='Chat'
                            onClick={() => navigateTo('/chats')}
                        >
                            <FontAwesomeIcon icon={faComments} />
                            <span className='font-semibold text-sm whitespace-nowrap'>
                                Chat
                            </span>
                        </button>
                    )}

                    {isFeatureEnabled(
                        Features.EXTRACTION,
                        featureFlagsData
                    ) && (
                        <button
                            className={`h-10 px-3 flex items-center justify-center gap-2 shrink-0 border ${
                                path === '/extraction'
                                    ? 'bg-backgrounds-primary100 text-texts-tertiary shadow-inner border-backgrounds-primary200'
                                    : 'text-texts-secondary300 bg-backgrounds-white border-transparent'
                            } rounded-xl hover:bg-backgrounds-primary100 hover:border-backgrounds-primary200`}
                            title='Extraction'
                            onClick={() => navigateTo('/extraction')}
                        >
                            <FontAwesomeIcon icon={faFileExport} />
                            <span className='font-semibold text-sm whitespace-nowrap'>
                                Extraction
                            </span>
                        </button>
                    )}

                    {visibleBeforeTasks.map((item) => (
                        <button
                            key={item.key}
                            className={`h-10 px-3 flex items-center justify-center gap-2 shrink-0 border ${
                                path === item.path
                                    ? 'bg-backgrounds-primary100 text-texts-tertiary shadow-inner border-backgrounds-primary200'
                                    : 'text-texts-secondary300 bg-backgrounds-white border-transparent'
                            } rounded-xl hover:bg-backgrounds-primary100 hover:border-backgrounds-primary200`}
                            title={item.label}
                            onClick={() => navigateTo(item.path)}
                        >
                            <FontAwesomeIcon icon={item.icon} />
                            <span className='font-semibold text-sm whitespace-nowrap'>
                                {item.label}
                            </span>
                        </button>
                    ))}

                    <div className='relative shrink-0'>
                        <button
                            className={`h-10 px-3 flex items-center justify-center gap-2 border ${
                                isTaskNotification
                                    ? 'text-texts-secondary300 bg-backgrounds-white border-icons-warning'
                                    : path === '/tasks'
                                      ? 'bg-backgrounds-primary100 text-texts-tertiary shadow-inner border-backgrounds-primary200'
                                      : 'text-texts-secondary300 bg-backgrounds-white border-transparent'
                            } rounded-xl hover:bg-backgrounds-primary100 hover:border-backgrounds-primary200`}
                            title='Tasks'
                            onClick={() => {
                                // Only clear a task notification here; clearing
                                // unconditionally would wipe an unread bell alert.
                                if (props.setNewNotification && isTaskNotification) {
                                    props.setNewNotification(null);
                                }
                                navigateTo('/tasks');
                            }}
                        >
                            <FontAwesomeIcon icon={faListCheck} />
                            <span className='font-semibold text-sm whitespace-nowrap'>
                                Tasks
                            </span>
                        </button>
                        {isTaskNotification && (
                            <span className='absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-icons-warning animate-pulse' />
                        )}
                    </div>

                    {visibleAfterTasks.map((item) => (
                        <button
                            key={item.key}
                            className={`h-10 px-3 flex items-center justify-center gap-2 shrink-0 border ${
                                path === item.path
                                    ? 'bg-backgrounds-primary100 text-texts-tertiary shadow-inner border-backgrounds-primary200'
                                    : 'text-texts-secondary300 bg-backgrounds-white border-transparent'
                            } rounded-xl hover:bg-backgrounds-primary100 hover:border-backgrounds-primary200`}
                            title={item.label}
                            onClick={() => navigateTo(item.path)}
                        >
                            <FontAwesomeIcon icon={item.icon} />
                            <span className='font-semibold text-sm whitespace-nowrap'>
                                {item.label}
                            </span>
                        </button>
                    ))}

                    {moreMenuItems.length > 0 && (
                        <Dropdown
                            dropDownList={moreMenuItems}
                            icon={<FontAwesomeIcon icon={faEllipsisV} />}
                            showButtonText={true}
                            buttonText='More'
                            listTitleText='More'
                            buttonHoverText='More'
                            buttonClassName={`!h-10 shrink-0 border px-3 ${
                                isInMoreMenu
                                    ? '!bg-backgrounds-primary100 !text-texts-tertiary !border-backgrounds-primary200'
                                    : '!text-texts-secondary300 !bg-backgrounds-white !border-transparent'
                            }`}
                            placement='bottom'
                        />
                    )}
                </div>

                {/* Offscreen clone of every possible nav item, used only to
                    measure real rendered widths — see recomputeVisibleFlexCount. */}
                <div
                    style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
                    aria-hidden='true'
                >
                    <div
                        ref={measureContainerRef}
                        className='flex items-center gap-1.5 px-2 py-1.5'
                    >
                        {fixedNavItems.map((item, index) => (
                            <MeasureNavButton
                                key={item.key}
                                ref={(el) => (fixedMeasureRefs.current[index] = el)}
                                icon={item.icon}
                                label={item.label}
                            />
                        ))}
                        {flexNavItems.map((item, index) => (
                            <MeasureNavButton
                                key={item.key}
                                ref={(el) => (flexMeasureRefs.current[index] = el)}
                                icon={item.icon}
                                label={item.label}
                            />
                        ))}
                        <MeasureNavButton
                            ref={moreMeasureRef}
                            icon={faEllipsisV}
                            label='More'
                        />
                    </div>
                </div>
            </div>

            <div className='lg:hidden relative'>
                <div className='flex items-center'>
                    {isSmallScreen &&
                        isFeatureEnabled(
                            Features.NOTIFICATION,
                            featureFlagsData
                        ) && (
                            <NotificationBell
                                viewNotifications={props.viewNotifications}
                                setViewNotifications={
                                    props.setViewNotifications
                                }
                                newNotification={props.newNotification}
                                setNewNotification={props.setNewNotification}
                                messages={props.messages}
                                socket={props.socket}
                            />
                        )}

                    <button className='ml-auto' onClick={toggleMenu}>
                        <FontAwesomeIcon
                            icon={faBars}
                            className='text-2xl text-backgrounds-primary200'
                        />
                    </button>
                </div>

                {isMenuOpen && (
                    <div
                        className='fixed inset-0 bg-backgrounds-black bg-opacity-75 z-50'
                        onClick={closeMenu}
                    >
                        <div className='fixed top-0 right-0 bottom-0 w-64 bg-backgrounds-white shadow-lg z-50 overflow-y-auto'>
                            <div className='p-4 bg-backgrounds-primary100 font-semibold text-lg text-texts-secondary flex flex-row'>
                                ACTIONS
                            </div>

                            {/* Model selection dropdown in mobile menu */}
                            {isFeatureEnabled(
                                [
                                    Features.OTHERS,
                                    OtherFeatures.MODEL_SELECTION,
                                ],
                                featureFlagsData
                            ) && (
                                <div
                                    className='p-4 border-t border-backgrounds-border'
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className='flex items-center font-semibold mb-2'>
                                        <FontAwesomeIcon
                                            icon={faRobot}
                                            className='mr-2 text-texts-secondary300'
                                        />
                                        Model
                                    </div>
                                    <Dropdown
                                        dropDownList={modelDropdownItems}
                                        triggerComponent={
                                            <button
                                                type='button'
                                                className='w-full max-w-full p-2 rounded-lg bg-backgrounds-white border border-backgrounds-primary200 text-texts-secondary300 flex items-center justify-between overflow-hidden'
                                            >
                                                <span className='truncate'>
                                                    {isLoadingModels
                                                        ? 'Loading...'
                                                        : selectedTier ===
                                                                ModelTiers.CUSTOM &&
                                                            customModel
                                                          ? customModel
                                                          : selectedTier}
                                                </span>
                                                <FontAwesomeIcon
                                                    icon={faCaretDown}
                                                    className='ml-1.5 text-xs'
                                                />
                                            </button>
                                        }
                                        placement='bottom'
                                        listTitleText='Select Model'
                                    />
                                </div>
                            )}

                            {isFeatureEnabled(
                                Features.CHATS,
                                featureFlagsData
                            ) && (
                                <button
                                    className={`p-4 border-t border-backgrounds-border w-full text-left ${
                                        path === '/chats'
                                            ? 'bg-backgrounds-primary100 text-texts-tertiary'
                                            : 'text-texts-secondary300'
                                    }`}
                                    onClick={() => {
                                        if (transactionMode) {
                                            setTransactionMode(
                                                !transactionMode
                                            );
                                        }
                                        if (path !== '/chats') {
                                            safeNavigate('/chats');
                                            setIsMenuOpen(false);
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faComments}
                                        className='pr-1.5'
                                    />
                                    Chat
                                </button>
                            )}

                            {isFeatureEnabled(
                                Features.EXTRACTION,
                                featureFlagsData
                            ) && (
                                <button
                                    className={`p-4 border-t border-backgrounds-border w-full text-left ${
                                        path === '/extraction'
                                            ? 'bg-backgrounds-primary100 text-texts-tertiary'
                                            : 'text-texts-secondary300'
                                    }`}
                                    onClick={() => {
                                        if (transactionMode) {
                                            setTransactionMode(
                                                !transactionMode
                                            );
                                        }
                                        if (path !== '/extraction') {
                                            safeNavigate('/extraction');
                                            setIsMenuOpen(false);
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faFileExport}
                                        className='pr-1.5'
                                    />
                                    Extraction
                                </button>
                            )}

                            {isFeatureEnabled(
                                Features.TEMPLATE,
                                featureFlagsData
                            ) && (
                                <button
                                    className={`p-4 border-t border-backgrounds-border w-full text-left ${
                                        path === '/templates-and-layouts'
                                            ? 'bg-backgrounds-primary100 text-texts-tertiary'
                                            : 'text-texts-secondary300'
                                    }`}
                                    onClick={() => {
                                        if (transactionMode) {
                                            setTransactionMode(
                                                !transactionMode
                                            );
                                        }
                                        if (path !== '/templates-and-layouts') {
                                            safeNavigate(
                                                '/templates-and-layouts'
                                            );
                                            setIsMenuOpen(false);
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faPieChart}
                                        className='pr-1.5'
                                    />
                                    {/* Report Designer */}
                                    Templates/Layouts
                                </button>
                            )}

                            {isFeatureEnabled(
                                Features.SUPPORT,
                                featureFlagsData
                            ) && (
                                <button
                                    className={`p-4 border-t border-backgrounds-border w-full text-left ${
                                        path === '/support'
                                            ? 'bg-backgrounds-primary100 text-texts-tertiary'
                                            : 'text-texts-secondary300'
                                    }`}
                                    onClick={() => {
                                        if (transactionMode) {
                                            setTransactionMode(
                                                !transactionMode
                                            );
                                        }
                                        if (path !== '/help') {
                                            safeNavigate('/help');
                                            setIsMenuOpen(false);
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faHeadset}
                                        className='pr-1.5'
                                    />
                                    Help
                                </button>
                            )}

                            <button
                                className={`p-4 border-t border-backgrounds-border w-full text-left ${
                                    path === '/tasks'
                                        ? 'bg-backgrounds-primary100 text-texts-tertiary'
                                        : 'text-texts-secondary300'
                                }`}
                                onClick={() => {
                                    if (transactionMode) {
                                        setTransactionMode(!transactionMode);
                                    }
                                    if (path !== '/tasks') {
                                        safeNavigate('/tasks');
                                        setIsMenuOpen(false);
                                    }
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faListCheck}
                                    className='pr-1.5'
                                />
                                Tasks
                            </button>

                            {isOrgAdmin && (
                                <button
                                    className={`p-4 border-t border-backgrounds-border w-full text-left ${
                                        path === '/agents-updates'
                                            ? 'bg-backgrounds-primary100 text-texts-tertiary'
                                            : 'text-texts-secondary300'
                                    }`}
                                    onClick={() => {
                                        if (transactionMode) {
                                            setTransactionMode(
                                                !transactionMode
                                            );
                                        }
                                        if (path !== '/agents-updates') {
                                            safeNavigate('/agents-updates');
                                            setIsMenuOpen(false);
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faSync}
                                        className='pr-1.5'
                                    />
                                    Agent Logs
                                </button>
                            )}

                            {isFeatureEnabled(
                                Features.SETTINGS,
                                featureFlagsData
                            ) && (
                                <button
                                    className={`p-4 border-t border-backgrounds-border w-full text-left ${
                                        path === '/settings'
                                            ? 'bg-backgrounds-primary100 text-texts-tertiary'
                                            : ''
                                    }`}
                                    onClick={() => {
                                        if (transactionMode) {
                                            setTransactionMode(
                                                !transactionMode
                                            );
                                        }
                                        if (path !== '/settings') {
                                            safeNavigate('/settings');
                                            setIsMenuOpen(false);
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faGear}
                                        className='pr-1.5'
                                    />
                                    Settings
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <div className='hidden lg:block'>
                <div className='ml-auto flex items-center justify-center'>
                    {/* Model Selection Dropdown */}
                    {isFeatureEnabled(
                        [Features.OTHERS, OtherFeatures.MODEL_SELECTION],
                        featureFlagsData
                    ) && (
                        <Dropdown
                            dropDownList={modelDropdownItems}
                            showButtonText={true}
                            buttonTitle={
                                <>
                                    <FontAwesomeIcon
                                        icon={faRobot}
                                        className='pr-2'
                                    />
                                    Model
                                </>
                            }
                            buttonHoverText={`Selected AI Model: ${
                                isLoadingModels
                                    ? 'Loading...'
                                    : selectedTier === ModelTiers.CUSTOM &&
                                        customModel
                                      ? customModel
                                      : selectedTier
                            }`}
                            buttonText={
                                isLoadingModels
                                    ? 'Loading...'
                                    : selectedTier === ModelTiers.CUSTOM &&
                                        customModel
                                      ? customModel
                                      : selectedTier
                            }
                            buttonClassName='max-w-[260px] overflow-hidden'
                            placement='bottomRight'
                            listTitleText='Select AI Model'
                        />
                    )}

                    {!isSmallScreen &&
                        isFeatureEnabled(
                            Features.NOTIFICATION,
                            featureFlagsData
                        ) && (
                            <>
                                <div className='flex items-center gap-2 ml-2'>
                                    <NotificationBell
                                        viewNotifications={
                                            props.viewNotifications
                                        }
                                        setViewNotifications={
                                            props.setViewNotifications
                                        }
                                        newNotification={props.newNotification}
                                        setNewNotification={
                                            props.setNewNotification
                                        }
                                        messages={props.messages}
                                        socket={props.socket}
                                    />
                                </div>
                            </>
                        )}

                    {isFeatureEnabled(Features.SETTINGS, featureFlagsData) && (
                        <Button
                            className={`w-9 h-9 hover:border-texts-tertiary200 hover:bg-backgrounds-primary100 ${
                                path === '/settings'
                                    ? 'bg-backgrounds-primary100 text-texts-tertiary400'
                                    : 'text-texts-secondary300'
                            }`}
                            onClick={() => {
                                if (transactionMode) {
                                    setTransactionMode(!transactionMode);
                                }
                                if (path !== '/settings') {
                                    safeNavigate('/settings');
                                }
                            }}
                            hoverText={'Settings'}
                            type={'text'}
                            icon={faGear}
                        />
                    )}
                </div>
            </div>
            {/* Sidebar Overlay */}
            {isSidebarOpen && (
                <div
                    className='fixed inset-0 bg-backgrounds-white z-50 '
                    onClick={closeSidebar}
                ></div>
            )}
            {/* Render Sidebar */}
            {isSidebarOpen && (
                <>
                    <div
                        className='fixed inset-0 bg-backgrounds-white z-50'
                        onClick={closeSidebar}
                    ></div>
                    <div className='fixed inset-0 z-50'>
                        {path === '/chats' &&
                        isFeatureEnabled(Features.CHATS, featureFlagsData) ? (
                            <Sidebar
                                isAdmin={props.isAdmin}
                                chatrepo={props.repo_to_chat}
                                chatrepofunction={props.updateRepoToChat}
                                set_fine_tuned={props.set_fine_tuned_chat}
                                fine_tune={props.fine_tuned_chat}
                                fileToChat={props.fileToChat}
                                setFileToChat={props.setFileToChat}
                                setIsRepoViewExpanded={
                                    props.setIsRepoViewExpanded
                                }
                                setIsAdmin={props.setIsAdmin}
                                mode={props.mode}
                                isSidebarOpen={isSidebarOpen}
                                closeSidebar={closeSidebar}
                                setIsSidebarOpen={setIsSidebarOpen}
                                processingRepos={props.processingRepos}
                                setViewNotifications={
                                    props.setViewNotifications
                                }
                                socket={props.socket}
                                togglePdfVisibility={props.togglePdfVisibility}
                                isSmallScreen={props.isSmallScreen}
                                {...(props.isRepoSortedAlphabetically !==
                                    undefined && {
                                    isRepoSortedAlphabetically:
                                        props.isRepoSortedAlphabetically,
                                    setIsRepoSortedAlphabetically:
                                        props.setIsRepoSortedAlphabetically,
                                })}
                            />
                        ) : (
                            <>
                                {path === '/extraction' &&
                                isFeatureEnabled(
                                    Features.EXTRACTION,
                                    featureFlagsData
                                ) ? (
                                    <Sidebar
                                        chatrepo={props.chatrepo}
                                        chatrepofunction={
                                            props.chatrepofunction
                                        }
                                        set_fine_tuned={props.set_fine_tuned}
                                        fine_tune={props.fine_tune}
                                        fileToChat={props.fileToChat}
                                        setFileToChat={props.setFileToChat}
                                        setIsRepoViewExpanded={
                                            props.setIsRepoViewExpanded
                                        }
                                        isAdmin={props.isAdmin}
                                        setIsAdmin={props.setIsAdmin}
                                        mode={props.mode}
                                        isSidebarOpen={isSidebarOpen}
                                        closeSidebar={closeSidebar}
                                        setIsSidebarOpen={setIsSidebarOpen}
                                        processingRepos={props.processingRepos}
                                        setViewNotifications={
                                            props.setViewNotifications
                                        }
                                        socket={props.socket}
                                        togglePdfVisibility={
                                            props.togglePdfVisibility
                                        }
                                        isSmallScreen={props.isSmallScreen}
                                        {...(props.isRepoSortedAlphabetically !==
                                            undefined && {
                                            isRepoSortedAlphabetically:
                                                props.isRepoSortedAlphabetically,
                                            setIsRepoSortedAlphabetically:
                                                props.setIsRepoSortedAlphabetically,
                                        })}
                                    />
                                ) : (
                                    <>
                                        {path === '/help' &&
                                        isFeatureEnabled(
                                            Features.SUPPORT,
                                            featureFlagsData
                                        ) ? (
                                            <SupportSideBar
                                                topics={props.topics}
                                                activeSection={
                                                    props.activeSection
                                                }
                                                onSectionSelect={
                                                    props.onSectionSelect
                                                }
                                                setIsLeftViewExpanded={
                                                    props.setIsLeftViewExpanded
                                                }
                                                isSidebarOpen={isSidebarOpen}
                                                closeSidebar={closeSidebar}
                                                setIsSidebarOpen={
                                                    setIsSidebarOpen
                                                }
                                                socket={props.socket}
                                                displayFloatingButton={
                                                    props.displayFloatingButton
                                                }
                                                setDisplayFloatingButton={
                                                    props.setDisplayFloatingButton
                                                }
                                            />
                                        ) : (
                                            <>
                                                {path ===
                                                    '/templates-and-layouts' &&
                                                isFeatureEnabled(
                                                    Features.TEMPLATE,
                                                    featureFlagsData
                                                ) ? (
                                                    <ReportLayoutsSideView
                                                        templateList={
                                                            props.templateList
                                                        }
                                                        selectedTemplate={
                                                            props.selectedTemplate
                                                        }
                                                        setSelectedTemplate={
                                                            props.setSelectedTemplate
                                                        }
                                                        selectedLayout={
                                                            props.selectedLayout
                                                        }
                                                        setSelectedLayout={
                                                            props.setSelectedLayout
                                                        }
                                                        setIsRepoViewExpanded={
                                                            props.setIsRepoViewExpanded
                                                        }
                                                        isSidebarOpen={
                                                            isSidebarOpen
                                                        }
                                                        closeSidebar={
                                                            closeSidebar
                                                        }
                                                        setIsSidebarOpen={
                                                            setIsSidebarOpen
                                                        }
                                                        socket={props.socket}
                                                        setTemplateList={
                                                            props.setTemplateList
                                                        }
                                                        fetchTemplateList={
                                                            props.fetchTemplateList
                                                        }
                                                        setActiveTemplate={
                                                            props.setActiveTemplate
                                                        }
                                                        setAddNewTemplateMode={
                                                            props.setAddNewTemplateMode
                                                        }
                                                        templateData={
                                                            props.templateData
                                                        }
                                                        setTemplateData={
                                                            props.setTemplateData
                                                        }
                                                        handleLoadTemplate={
                                                            props.handleLoadTemplate
                                                        }
                                                        setRetrievedTemplateData={
                                                            props.setRetrievedTemplateData
                                                        }
                                                    />
                                                ) : (
                                                    <div>
                                                        {isFeatureEnabled(
                                                            Features.SETTINGS,
                                                            featureFlagsData
                                                        ) && (
                                                            <SettingsSideBar
                                                                settingSections={
                                                                    props.settingSections
                                                                }
                                                                activeSection={
                                                                    props.activeSection
                                                                }
                                                                setActiveSection={
                                                                    props.setActiveSection
                                                                }
                                                                setIsLeftViewExpanded={
                                                                    props.setIsLeftViewExpanded
                                                                }
                                                                isSidebarOpen={
                                                                    isSidebarOpen
                                                                }
                                                                closeSidebar={
                                                                    closeSidebar
                                                                }
                                                                setIsSidebarOpen={
                                                                    setIsSidebarOpen
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default Navbar;
